import { useState, useEffect } from 'react';

interface PopupBannerProps {
  onClose?: () => void;
}

export default function PopupBanner({ onClose }: PopupBannerProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // ページ読み込み後5秒で表示
    const timer = setTimeout(() => {
      const hasSeenPopup = sessionStorage.getItem('hasSeenPopup');
      if (!hasSeenPopup) {
        setIsVisible(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    sessionStorage.setItem('hasSeenPopup', 'true');
    if (onClose) onClose();
  };

  const handleCTA = () => {
    handleClose();
    const contactSection = document.querySelector('#contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-[100] animate-fade-in"
        onClick={handleClose}
      ></div>

      {/* Popup */}
      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[101] w-[90%] max-w-lg animate-scale-in">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors z-10 cursor-pointer"
            aria-label="閉じる"
          >
            <i className="ri-close-line text-2xl text-gray-600"></i>
          </button>

          {/* Content */}
          <div className="relative">
            {/* Background Image */}
            <div className="relative h-48 w-full overflow-hidden">
              <img 
                src="https://public.readdy.ai/ai/img_res/9912069c4d642e71bd963012f8f25b63.jpg"
                alt="系統用蓄電池投資"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
              
              {/* Badge */}
              <div className="absolute top-4 left-4">
                <div className="bg-amber-500 text-white px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap shadow-lg">
                  🎯 期間限定キャンペーン
                </div>
              </div>
            </div>

            {/* Text Content */}
            <div className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">
                今なら無料相談実施中！
              </h3>
              <p className="text-base text-gray-700 mb-2 text-center leading-relaxed">
                系統用蓄電池投資に関する
              </p>
              <p className="text-base text-gray-700 mb-6 text-center leading-relaxed">
                詳しい資料とシミュレーションを無料でご提供
              </p>

              {/* Benefits */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center bg-teal-600 rounded-full mr-3 flex-shrink-0">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span className="text-sm text-gray-700">想定年間利回り8〜12%の投資機会</span>
                </div>
                <div className="flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center bg-teal-600 rounded-full mr-3 flex-shrink-0">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span className="text-sm text-gray-700">東京電力管内に特化した戦略的投資</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleCTA}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white px-6 py-4 rounded-lg text-base font-bold transition-all whitespace-nowrap cursor-pointer shadow-lg"
                >
                  今すぐ無料相談する
                </button>
                <button
                  onClick={handleClose}
                  className="w-full bg-white hover:bg-gray-50 text-gray-600 px-6 py-3 rounded-lg text-sm font-semibold transition-all border border-gray-300 whitespace-nowrap cursor-pointer"
                >
                  後で見る
                </button>
              </div>

              {/* Note */}
              <p className="text-xs text-gray-500 text-center mt-4">
                ※このポップアップは1回のみ表示されます
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }

        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
